import { PROVS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import {
  clearPendingProviderUser,
  extractTokenFromPayload,
  normalizeProviderUser,
  savePendingProviderUser,
} from "../utils/normalizeProviderUser";
import {
  mockRequestLoginOtp,
  mockVerifyLoginOtp,
  mockResendLoginOtp,
} from "./mockAuth";

function toEmailOrPhone(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed.includes("@") ? trimmed.toLowerCase() : trimmed;
}

/**
 * Build Redux login payload from verify response inner `data`.
 */
function buildLoginResult(payload, loginEmail) {
  const token = extractTokenFromPayload(payload);
  const user = normalizeProviderUser(payload, loginEmail);

  if (!user?.email) {
    throw new Error("Unexpected response from login service.");
  }
  if (!token) {
    throw new Error("Login succeeded but no access token was returned.");
  }

  return {
    token,
    token_type: payload?.token_type ?? "Bearer",
    expires_in: payload?.expires_in ?? null,
    user,
  };
}

/**
 * Step 1 — POST /api/provs/auth/request
 * Body: { email }
 */
export async function requestLoginOtp(email) {
  const normalizedEmail = String(email ?? "").trim().toLowerCase();

  if (!normalizedEmail.includes("@")) {
    throw new Error("Enter a valid email address.");
  }

  if (USE_MOCK_AUTH) {
    return mockRequestLoginOtp(normalizedEmail);
  }

  const json = await apiRequest(PROVS_AUTH.request, {
    method: "POST",
    body: { email: normalizedEmail },
  });

  const payload = unwrapApiEnvelope(json);
  const user = normalizeProviderUser(payload, normalizedEmail);
  if (user) savePendingProviderUser(user);

  return {
    email: normalizedEmail,
    channel: "email",
    expires_in: 600,
    user,
    message: json?.data?.message ?? "Verification code sent.",
  };
}

/**
 * Step 2 — POST /api/provs/auth/verify
 * Body: { email, otp }
 */
export async function verifyLoginOtp(email, otp) {
  const normalizedEmail = String(email ?? "").trim().toLowerCase();

  if (USE_MOCK_AUTH) {
    const result = await mockVerifyLoginOtp(normalizedEmail, otp);
    clearPendingProviderUser();
    return result;
  }

  const json = await apiRequest(PROVS_AUTH.verify, {
    method: "POST",
    body: { email: normalizedEmail, otp: String(otp).trim() },
  });

  const payload = unwrapApiEnvelope(json);
  const result = buildLoginResult(payload, normalizedEmail);
  clearPendingProviderUser();
  return result;
}

/**
 * Resend OTP — POST /api/ops/auth/resend-otp
 * Body: { emailorPhone }
 */
export async function resendLoginOtp(emailOrPhone) {
  const identifier = toEmailOrPhone(emailOrPhone);

  if (USE_MOCK_AUTH) {
    return mockResendLoginOtp(identifier.includes("@") ? identifier : emailOrPhone);
  }

  const json = await apiRequest(PROVS_AUTH.resend, {
    method: "POST",
    body: { emailorPhone: identifier },
  });

  const payload = unwrapApiEnvelope(json);
  const user = normalizeProviderUser(payload, identifier.includes("@") ? identifier : "");
  if (user) savePendingProviderUser(user);

  return {
    email: user?.email ?? (identifier.includes("@") ? identifier : emailOrPhone),
    channel: identifier.includes("@") ? "email" : "phone",
    expires_in: 600,
    user,
    message: json?.data?.message ?? "A new verification code has been sent.",
  };
}

/** Logout — POST /api/provs/logout with Bearer token, no body. */
export async function logoutProviderUser(token) {
  clearPendingProviderUser();

  if (USE_MOCK_AUTH) return;
  if (!token) return;

  try {
    await apiRequest(PROVS_AUTH.logout, {
      method: "POST",
      token,
    });
  } catch {
    // Local session is still cleared even if the API call fails.
  }
}

export { USE_MOCK_AUTH };
